import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Card, Button, Icon, toast } from '@/components/ui';
import { getInvoice, uploadInvoices } from '@/lib/invoices';
import { MAX_FILE_BYTES, MAX_FILES_PER_UPLOAD, SUPPORTED_MIME } from '@/types/invoice';
import { humanSize } from '@/lib/format';
import { en } from '@/i18n/en';

interface RowState {
  id: string;
  file: File;
  status: 'queued' | 'uploading' | 'processing' | 'ready' | 'failed';
  progress: number;
  invoiceId?: string;
  error?: string;
}

const ALLOWED_EXTS = ['.pdf', '.png', '.jpg', '.jpeg', '.tif', '.tiff', '.bmp', '.heic', '.docx', '.doc', '.xlsx', '.xls'];

function isAllowed(file: File): { ok: boolean; reason?: string } {
  if (file.size === 0) return { ok: false, reason: 'Fichier vide' };
  if (file.size > MAX_FILE_BYTES) return { ok: false, reason: `Dépasse ${humanSize(MAX_FILE_BYTES)}` };
  const lname = file.name.toLowerCase();
  const extOk = ALLOWED_EXTS.some((e) => lname.endsWith(e));
  const mimeOk = file.type === '' || SUPPORTED_MIME[file.type] !== undefined;
  if (!extOk && !mimeOk) return { ok: false, reason: 'Format de fichier non pris en charge' };
  return { ok: true };
}

export function UploadPage() {
  const t = en.dashboard.upload;
  const inputRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<RowState[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const pendingInvoiceIds = rows
    .filter((r) => r.status === 'processing' && r.invoiceId)
    .map((r) => r.invoiceId!)
    .join(',');

  useEffect(() => {
    if (!pendingInvoiceIds) return;
    const ids = pendingInvoiceIds.split(',');
    let cancelled = false;
    const refresh = async () => {
      const invoices = await Promise.all(ids.map((id) => getInvoice(id).catch(() => null)));
      if (cancelled) return;
      let completed = false;
      setRows((current) => current.map((row) => {
        if (!row.invoiceId) return row;
        const invoice = invoices.find((item) => item?.id === row.invoiceId);
        if (!invoice || invoice.status === 'processing') return row;
        completed = true;
        return {
          ...row,
          status: invoice.status === 'ready' ? 'ready' : 'failed',
          error: invoice.status === 'failed' ? (invoice.raw?.ocrText || "Échec de l'analyse de la facture.") : undefined,
        };
      }));
      if (completed) qc.invalidateQueries({ queryKey: ['invoices'] });
    };
    void refresh();
    const timer = window.setInterval(() => { void refresh(); }, 2000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [pendingInvoiceIds, qc]);

  const accept = useCallback((files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    if (rows.length + list.length > MAX_FILES_PER_UPLOAD) {
      toast(`Maximum ${MAX_FILES_PER_UPLOAD} fichiers par envoi.`, 'danger');
      return;
    }
    const next: RowState[] = [];
    for (const file of list) {
      const check = isAllowed(file);
      next.push({
        id: `${file.name}-${file.size}-${file.lastModified}`,
        file,
        status: check.ok ? 'queued' : 'failed',
        progress: 0,
        error: check.reason,
      });
    }
    setRows((prev) => [...prev, ...next]);
  }, [rows.length]);

  async function startUpload() {
    const queued = rows.filter((r) => r.status === 'queued');
    if (queued.length === 0) return;
    setBusy(true);
    try {
      setRows((rs) => rs.map((r) => r.status === 'queued' ? { ...r, status: 'uploading' as const } : r));
      const created = await uploadInvoices(
        queued.map((r) => r.file),
        (loaded, total) => {
          const pct = Math.round((loaded / total) * 100);
          setRows((rs) => rs.map((r) => r.status === 'uploading' ? { ...r, progress: pct } : r));
        },
      );
      // Map created stubs back to rows in order.
      setRows((rs) => {
        const result = [...rs];
        let ci = 0;
        for (let i = 0; i < result.length; i++) {
          if (result[i].status === 'uploading' && ci < created.length) {
            const stub = created[ci++];
            result[i] = {
              ...result[i],
              status: stub.status === 'ready' ? 'ready' : 'processing',
              progress: 100,
              invoiceId: stub.id,
            };
          }
        }
        return result;
      });
      toast(`${created.length} facture${created.length > 1 ? 's' : ''} téléversée${created.length > 1 ? 's' : ''}.`, 'success');
      qc.invalidateQueries({ queryKey: ['invoices'] });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Échec du téléversement';
      toast(msg, 'danger');
      setRows((rs) => rs.map((r) => r.status === 'uploading' ? { ...r, status: 'failed' as const, error: msg } : r));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack-lg">
      <h1 style={{ margin: 0, fontSize: 28, letterSpacing: '-0.025em' }}>{t.title}</h1>

      <Card>
        <div
          className={`file-drop file-drop-lg ${dragActive ? 'active' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => { e.preventDefault(); setDragActive(false); accept(e.dataTransfer.files); }}
        >
          <span className="file-icon"><Icon name="upload" size={20} /></span>
          <h3>{t.drop}</h3>
          <p>{t.pick}</p>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            accept=".pdf,image/*,.docx,.doc,.xlsx,.xls,.heic"
            onChange={(e) => e.target.files && accept(e.target.files)}
          />
          <Button variant="primary" size="sm" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}>
            Choisir des fichiers
          </Button>
        </div>

        {rows.length > 0 && (
          <>
            <div className="upload-list">
              {rows.map((r) => (
                <div key={r.id} className="row">
                  <span className="name">{r.file.name}</span>
                  <span className="muted" style={{ fontSize: 12 }}>{humanSize(r.file.size)}</span>
                  <div className="progress" aria-hidden><div style={{ width: `${r.progress}%` }} /></div>
                  <span className="status">
                    {r.status === 'failed' ? (r.error ?? 'Échoué')
                      : r.status === 'ready' ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: 'var(--success, #16a34a)', fontWeight: 600 }}>{t.ready}</span>
                          {r.invoiceId && (
                            <Link to={`/dashboard/invoices/${r.invoiceId}`} style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent, #ed5c25)' }}>
                              Voir &rarr;
                            </Link>
                          )}
                        </span>
                      )
                      : r.status === 'processing' ? t.processing
                      : r.status === 'uploading' ? `${r.progress}%`
                      : 'En attente'}
                  </span>
                </div>
              ))}
            </div>
            <p className="muted" style={{ fontSize: 13, marginTop: 12, marginBottom: 0 }}>
              Les factures téléversées sont enregistrées dans l'onglet <Link to="/dashboard/archive" style={{ textDecoration: 'underline' }}>Archive</Link>.
            </p>
            <div className="row-between" style={{ marginTop: 16 }}>
              <Button variant="ghost" onClick={() => setRows([])} disabled={busy}>Effacer</Button>
              <Button variant="primary" onClick={startUpload} disabled={busy || rows.every((r) => r.status !== 'queued')}>
                {busy ? '…' : 'Téléverser'}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
